import type * as Api from '@ViewModels';
import { v4 as uuid } from 'uuid';
import { isSurveySection } from '../types';
import type { QuestionVisibility, ShortAnswerQuestion, SurveyPart, SurveyQuestion, SurveySection } from '../types';

/**
 * Whether a SurveyForm contains at least one V2 SurveySection. Used to gate
 * V2-specific initialization behavior (phantom-field seeding for V1 vs empty
 * fields for V2).
 */
export function isV2SurveyForm(
	surveyForm: { surveyParts?: ReadonlyArray<{ _type?: string }> } | undefined | null
): boolean {
	return surveyForm?.surveyParts?.some(p => isSurveySection(p as SurveyPart)) ?? false;
}

/**
 * Construct a freshly-seeded blank V2 question (Short Answer by default).
 *
 * Every blank V2 question is seeded with `id`, mirrored to `fieldId`, and the
 * `_type` discriminator set — so visibility eligibility filters
 * (SectionTriggers, QuestionVisibilityControls) can resolve this question as a
 * source the moment the user adds it.
 *
 * Consolidates the previously-duplicated inline seed shape used in
 * SectionQuestions, useSectionsFormArray, QuestionsWithSections and
 * CustomSurvey defaultValues.
 */
export function createBlankQuestion(): ShortAnswerQuestion {
	const id = uuid();
	return {
		_type: 'ShortAnswerQuestion',
		id,
		fieldId: id,
		label: '',
		// String literal matches the V1 `FormFieldType.String` enum value while
		// avoiding the heavy `@ViewModels` barrel import (it breaks `duplicate.spec.ts`
		// because the barrel pulls in viewmodels that need an unmocked runtime).
		fieldType: 'String',
		isHidden: false,
		// Required by default — matches CustomSurvey's `defaultOptional: false`
		// (the V1 "+ Add Question" default) so V2 section questions don't silently
		// start optional.
		isOptional: false,
	} as unknown as ShortAnswerQuestion;
}

/**
 * Re-hydrate a `SurveyForm` returned by the BE with the per-question data
 * (label, fieldType, isOptional, isHidden, options) that the BE strips from
 * `surveyParts` on read.
 *
 * V2 Custom Surveys persist both `customForm.fields[]` (the full field shape)
 * AND `surveyForm.surveyParts[]` (reference-only on read — the BE omits
 * `label`, `options`, and the flags). The FE treats `surveyParts[].questions[]`
 * as the source of truth for the editor, so dropping a reference-only shape
 * into RHF state flips controlled inputs to uncontrolled and visibly empties
 * the question label / options.
 *
 * This helper restores the canonical V2 shape by joining each question on its
 * `fieldId` to the corresponding `customForm.fields[]` entry.
 *
 * Pure: never mutates input. V1 surveys (no `SurveySection` parts) pass
 * through unchanged — the per-part early-return is the V1 compatibility
 * guarantee.
 */
export function denormalizeSurveyFormFromFields(
	surveyForm: Api.SurveyForm,
	fields: Api.IFormField<string>[] | undefined
): Api.SurveyForm {
	const fieldsById = new Map<string, Api.IFormField<string>>();
	for (const field of fields ?? []) {
		if (field.id) fieldsById.set(field.id, field);
	}

	const surveyParts = surveyForm.surveyParts.map(part => {
		// V1 pass-through: any non-section part returns unchanged. V1 surveys
		// never carry SurveySection parts, so the entire transform is a no-op
		// for them by construction.
		if (!isSurveySection(part as SurveyPart)) return part;

		const section = part as Api.SurveyPart & {
			questions?: Array<Api.SurveyQuestion & Partial<Api.IFormField<string>>>;
		};
		const questions = (section.questions ?? []).map(question => {
			const field = fieldsById.get(question.fieldId);
			if (!field) return question;
			return {
				...question,
				label: field.label,
				fieldType: field.fieldType,
				isOptional: field.isOptional,
				isHidden: field.isHidden,
				options: field.options ?? [],
			};
		});

		return { ...section, questions };
	});

	return { ...surveyForm, surveyParts };
}

/**
 * Convert a legacy V1 flat `SurveyQuestion[]` (as found in `SurveyForm.surveyParts`
 * before sections existed) into a V2 `SurveySection[]`.
 *
 * - The first section is a headerless wrapper around the legacy questions: no title,
 *   no description, so it renders visually identical to the V1 layout.
 * - The second section is a fresh empty section (empty-string title/description) —
 *   this is the section the user just chose to add by clicking `+ Add Section` in V1.
 *   Seeded with one blank question to match `appendEmptySection`'s pattern and
 *   satisfy the BE's ≥1-question-per-section invariant by default.
 *
 * Called when the user upgrades a legacy survey to V2 from inside the V1 editor.
 */
export function v1ToV2(parts: SurveyQuestion[]): SurveySection[] {
	return [
		{
			_type: 'SurveySection',
			id: uuid(),
			title: undefined,
			description: undefined,
			questions: parts ?? [],
		},
		{
			_type: 'SurveySection',
			id: uuid(),
			title: '',
			description: '',
			questions: [createBlankQuestion()],
		},
	];
}

/**
 * Clone a section: assign fresh ids to the section and every question inside; rewrite
 * any per-question visibility rule that references an internal question id to the new
 * id (so visibility relationships within the section are preserved). External visibility
 * refs (those whose fieldId is NOT in the cloned question set) are dropped — clones
 * start as a clean slate for visibility per the spec. The section's own `visibleWhen`
 * is always dropped for the same reason.
 *
 * Triggered by the section …menu's Duplicate action.
 */
export function duplicateSection(source: SurveySection): SurveySection {
	const idMap = new Map<string, string>();
	// Each option on a cloned question also gets a fresh UUID — otherwise two
	// questions across the survey would share option ids, which the BE treats
	// as a uniqueness invariant for response attribution. `optionIdMap` lets us
	// rewrite `fieldValue` (option id) on any surviving internal visibility rule
	// so a cloned Q referencing a cloned sibling's option still resolves.
	const optionIdMap = new Map<string, string>();
	const newQuestions: SurveyQuestion[] = source.questions.map(q => {
		const newId = uuid();
		idMap.set(q.fieldId, newId);
		// `q.options` is already typed via SurveyQuestionBase → IFormField<string> as
		// CustomFormFieldOption[] (which includes the `fieldId` back-reference).
		const sourceOptions = q.options;
		const newOptions = sourceOptions?.map(opt => {
			const newOptId = uuid();
			if (opt.id) optionIdMap.set(opt.id, newOptId);
			// Update both `id` (option's own pk) and `fieldId` (back-reference to the
			// parent question). Without re-keying `fieldId`, the cloned option still
			// points at the original question's id — the BE merges those options onto
			// the original field and stores the cloned field with zero options.
			return { ...opt, id: newOptId, fieldId: newId };
		});
		return { ...q, id: newId, fieldId: newId, ...(newOptions ? { options: newOptions } : {}) };
	});

	// Filter a visibility rule to internal-only references. External refs (those
	// whose fieldId is NOT in `idMap`) drop out entirely — clones start clean for
	// external visibility per the spec. For surviving internal Simple rules, both
	// the `fieldId` (target question) and `fieldValue` (target option id) get
	// remapped to the cloned counterparts so the rule still resolves.
	// Logical OR rules drop their external children; if exactly one survives,
	// collapse to Simple to match the writer convention used by
	// `cleanupVisibilityRules` in `validation.ts`.
	const stripExternalRefs = (v: QuestionVisibility | undefined): QuestionVisibility | undefined => {
		if (!v) return undefined;
		if (v._type === 'SimpleEqualityQuestionVisibility') {
			const mapped = idMap.get(v.fieldId);
			if (!mapped) return undefined;
			const newFieldValue = optionIdMap.get(v.fieldValue) ?? v.fieldValue;
			return { ...v, fieldId: mapped, fieldValue: newFieldValue };
		}
		const visibilities = v.visibilities.map(stripExternalRefs).filter((x): x is QuestionVisibility => !!x);
		if (visibilities.length === 0) return undefined;
		if (visibilities.length === 1) return visibilities[0];
		return { ...v, visibilities };
	};

	return {
		_type: 'SurveySection',
		id: uuid(),
		title: source.title,
		description: source.description,
		questions: newQuestions.map(q => ({ ...q, visibleWhen: stripExternalRefs(q.visibleWhen) })),
		// Section-level visibleWhen is always external — clones start without one.
		visibleWhen: undefined,
	};
}
